import React, { useCallback, useEffect, useState } from 'react';
import { compta, telechargerBlob } from '../services/api';
import { useNdf } from '../NdfContext';
import DetailNote from './DetailNote';
import './Notes.css';
import './Comptabilite.css';
import { eur } from '../services/format';

const LIBELLES_STATUT = { validee: 'Validée', transmise: 'Transmise', remboursee: 'Remboursée' };
const STATUTS = [
  { code: '', libelle: 'Tous statuts' },
  { code: 'validee', libelle: 'Validées (à traiter)' },
  { code: 'transmise', libelle: 'Transmises' },
  { code: 'remboursee', libelle: 'Remboursées' },
];

// L'écran de la comptabilité — même format maître-détail que Notes et
// Validation : la liste filtrable à gauche, l'aperçu de contrôle à droite,
// avec les actions comptables (Transmettre / Rembourser / ZIP) dans le volet.
function Comptabilite() {
  const { refs } = useNdf();
  const [liste, setListe] = useState([]);
  const [f, setF] = useState({ statut: '', periode: '', societe_id: '' });
  const [selection, setSelection] = useState(null); // id de note
  const [tick, setTick] = useState(0); // force le rechargement de l'aperçu
  const [erreur, setErreur] = useState('');
  const [occupe, setOccupe] = useState(false);

  const params = useCallback(() => {
    const p = {};
    if (f.statut) p.statut = f.statut;
    if (f.periode) p.periode = f.periode;
    if (f.societe_id) p.societe_id = f.societe_id;
    return p;
  }, [f]);

  const charger = useCallback(() => {
    compta.notes(params()).then((res) => setListe(res.data.notes)).catch(() => {});
  }, [params]);
  useEffect(() => { charger(); }, [charger]);

  const noteSel = liste.find((n) => n.id === selection) || null;

  const agir = async (fn, id) => {
    setErreur('');
    setOccupe(true);
    try {
      await fn(id);
      charger();
      setTick((t) => t + 1);
    } catch (err) {
      setErreur(err.response?.data?.error || 'Action impossible');
    } finally {
      setOccupe(false);
    }
  };

  const exporter = async (format) => {
    setErreur('');
    try {
      const res = await compta.exporter({ ...params(), format });
      telechargerBlob(res.data, `notes-de-frais_${f.periode || 'toutes'}.${format}`);
    } catch {
      setErreur('Export impossible');
    }
  };

  const zip = async (id) => {
    try {
      const res = await compta.zipJustificatifs(id);
      telechargerBlob(res.data, `note-${id}-justificatifs.zip`);
    } catch {
      setErreur('Aucun justificatif à télécharger sur cette note');
    }
  };

  const total = liste.reduce((s, n) => s + parseFloat(n.total_ttc || 0), 0);

  return (
    <div className="cp-layout">
      <div className={`cp-pane-liste ${selection ? 'cp-cache-mobile' : ''}`}>
        <h2 className="nf-titre-page">Comptabilité</h2>
        {!selection && erreur && <div className="nf-erreur-bandeau">{erreur}</div>}

        <div className="cp-filtres">
          <select value={f.statut} onChange={(e) => { setSelection(null); setF({ ...f, statut: e.target.value }); }}>
            {STATUTS.map((s) => <option key={s.code} value={s.code}>{s.libelle}</option>)}
          </select>
          <input type="month" value={f.periode} onChange={(e) => { setSelection(null); setF({ ...f, periode: e.target.value }); }} />
          <select value={f.societe_id} onChange={(e) => { setSelection(null); setF({ ...f, societe_id: e.target.value }); }}>
            <option value="">Toutes sociétés</option>
            {refs.societes.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        </div>
        <div className="cp-outils">
          <span className="cp-synthese">
            {liste.length} note{liste.length > 1 ? 's' : ''} · <span className="nf-montant">{eur(total)}</span>
          </span>
          <span className="cp-exports">
            <button type="button" className="nf-btn" onClick={() => exporter('xlsx')}>⬇ Excel</button>
            <button type="button" className="nf-btn" onClick={() => exporter('csv')}>⬇ CSV</button>
          </span>
        </div>

        {liste.length === 0 ? (
          <div className="nf-vide">
            <h2>Aucune note</h2>
            <p>Les notes validées par les N+1 arrivent ici, prêtes à être exportées.</p>
          </div>
        ) : (
          <ul className="nt-liste">
            {liste.map((n) => (
              <li key={n.id}>
                <button type="button"
                  className={`nt-carte ${selection === n.id ? 'nt-carte--active' : ''}`}
                  onClick={() => setSelection(n.id)}>
                  <span className="nt-carte-rangee">
                    <span className="nt-carte-gauche">
                      <span className="nt-carte-titre">{n.prenom} {n.nom || n.user_email}</span>
                      <span className="nt-carte-sous">
                        note nº {n.id} · {n.periode}{n.societe_nom ? ` · ${n.societe_nom}` : ''}
                        {n.dossier_libelle ? ` · 📁 ${n.dossier_libelle}` : ''} · {n.nb_lignes} ligne{n.nb_lignes > 1 ? 's' : ''}
                      </span>
                    </span>
                    <span className="nt-carte-cote">
                      <span className="nf-montant nt-carte-total">{eur(n.total_ttc)}</span>
                      <span className={`nf-statut nf-statut--${n.statut}`}>{LIBELLES_STATUT[n.statut] || n.statut}</span>
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={`cp-pane-detail ${selection ? '' : 'cp-cache-mobile'}`}>
        {selection ? (
          <>
            <div className="cp-actions-detail">
              <button type="button" className="fd-retour cp-retour-mobile" onClick={() => setSelection(null)}>← Notes</button>
              {noteSel?.statut === 'validee' && (
                <button type="button" className="nf-btn nf-btn--principal" disabled={occupe}
                  onClick={() => agir(compta.transmettre, selection)}>Transmettre à la comptabilité</button>
              )}
              {noteSel?.statut === 'transmise' && (
                <button type="button" className="nf-btn nf-btn--principal" disabled={occupe}
                  onClick={() => agir(compta.rembourser, selection)}>Marquer remboursée</button>
              )}
              <button type="button" className="nf-btn" onClick={() => zip(selection)}>ZIP justificatifs</button>
            </div>
            {erreur && <div className="nf-erreur-bandeau">{erreur}</div>}
            <DetailNote key={`${selection}:${tick}`} id={selection} integre onFermer={() => setSelection(null)} />
          </>
        ) : (
          <div className="nt-placeholder">
            <p>Sélectionnez une note à gauche pour la contrôler et la traiter.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Comptabilite;
